/**
 * This module contains all custom logic to suggest a prompt for the set of AI features
 * the user has enabled.
 *
 * To add a suggestion that the runner already supports, simply update this module
 * to return the new suggestion. See implementation and use of {@link getModelHubSuggestions} as an example.
 *
 * To add a a new kind of suggestion, you will also need to:
 * 1. Update the type in PromptSuggestionTypes.
 * 2. Implement an execution of the prompt in runPromptSuggestion.tsx.
 * 3. Update PromptSuggestionCards to render the new type of suggestion.
 *
 * NOTE:
 * - For NEW_CHAT, benchling-object input is only used for rendering. The object's name should be interpolated
 *   into the prompt.
 */
import {useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';

import {AgentMode} from '../sidepanel/ChatTypeUtil';
import {PromptSuggestion, PromptSuggestionCategory} from './PromptSuggestionTypes';
import {
  GetSuggestions,
  searchForRecentFile,
  useCachedResponse,
  useGetSuggestions,
} from './PromptSuggestionUtils';

/** @package exported for testing */
export async function getModelHubSuggestions(): Promise<Array<PromptSuggestion>> {
  if (!BenchlingConfig.MOLBIO_ENABLE_ALPHAFOLD_GA) {
    return [];
  }
  const protein = await searchForRecentFile('protein');
  if (protein == null) {
    return [];
  }
  return [
    {
      id: 'model-hub-open-3d-structure-for-recent-protein',
      summary: 'Run structure prediction',
      action: {
        input: {type: 'benchling-object', id: protein.id},
        type: 'MODEL_HUB_OPEN_3D_STRUCTURE',
      },
    },
  ];
}

/** @package exported for testing */
// eslint-disable-next-line require-await
export const getDataCaptureSuggestions: GetSuggestions = async () => {
  if (!BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT) {
    return [];
  }
  return [
    {
      id: 'data-capture-convert-example-protocol-to-entry',
      summary: 'Convert this protocol into a Benchling entry',
      action: {
        type: 'NEW_CHAT',
        agentMode: AgentMode.DATA_CAPTURE,
        input: {
          type: 'demo-file',
          filename: 'elisa-protocol-email.png',
          name: 'ELISA Protocol (Example).png',
        },
        prompt:
          'Create an entry from the protocol in the attached email. List the materials in a table format and include clear numbered steps.',
      },
    },
  ];
};

/** @package exported for testing */
export const getAskSuggestions: GetSuggestions = async () => {
  if (!BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH) {
    return [];
  }
  const recentEntry = await searchForRecentFile('entry');
  if (recentEntry == null) {
    return [];
  }
  return [
    {
      id: 'ask-write-summary-of-recent-entry',
      summary: 'Write a summary of my recent entry',
      action: {
        input: {type: 'benchling-object', id: recentEntry.id},
        type: 'NEW_CHAT',
        prompt: `Write a summary of my recent entry: ${recentEntry.name}`,
        agentMode: AgentMode.ASK,
      },
    },
  ];
};

/** @package exported for testing */
// eslint-disable-next-line require-await
export const getDeepResearchSuggestions: GetSuggestions = async () => {
  if (!BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH) {
    return [];
  }
  return [
    {
      id: 'deep-research-summarize-last-month-experiments',
      summary: 'Summarize my experiments from the last month into a report',
      action: {
        type: 'NEW_CHAT',
        agentMode: AgentMode.DEEP_RESEARCH,
        input: {type: 'none'},
        // This slightly longer prompt is more reliably getting the dates right
        prompt: 'Summarize my experiments going back from today over the last month into a report',
      },
    },
  ];
};

const CANNED_SUGGESTIONS_BY_SECTION: Record<PromptSuggestionCategory, Array<PromptSuggestion>> = {
  [PromptSuggestionCategory.FIND_DATA]: [
    {
      id: 'canned-antibody-lot-lineage',
      summary: 'Show me the lineage of antibody lot A143-2',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.ASK, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-find-sequences-cmv-promoter',
      summary: 'Find all sequences with a CMV promoter',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.ASK, prompt: '', input: {type: 'none'}},
    },

    {
      id: 'canned-historical-conditions-hela-cell-line',
      summary: 'Show all historical culture conditions for this HeLa cell line',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.ASK, prompt: '', input: {type: 'none'}},
    },
  ],
  [PromptSuggestionCategory.WRITE_REPORTS]: [
    {
      id: 'canned-report-key-outcomes-from-study',
      summary: 'Generate a nonclinical study report for Study-S-Alpha-143',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DEEP_RESEARCH, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-report-progress-program',
      summary: 'Write a progress report on Program Helix to share with my director',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DEEP_RESEARCH, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-report-executive-summary',
      summary: 'Make a CoA for Protein Lot PROT-023',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DEEP_RESEARCH, prompt: '', input: {type: 'none'}},
    },
  ],
  [PromptSuggestionCategory.DESIGN_EXPERIMENTS]: [
    {
      id: 'canned-design-elisa-protocol',
      summary: 'Draft an ELISA protocol using the best performing parameters found in scientific literature',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DATA_CAPTURE, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-design-qpcr-experiment',
      summary:
        'Design a qPCR experiment for my target gene across 3 conditions, based on similar ones my lab has run',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DATA_CAPTURE, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-design-from-photo-meeting-notes',
      summary: 'Convert this photo of my meeting notes with my PI into an organized experiment entry',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DATA_CAPTURE, prompt: '', input: {type: 'none'}},
    },
  ],
  [PromptSuggestionCategory.CAPTURE_DATA]: [
    {
      id: 'canned-import-cro-report',
      summary:
        'Import this CRO report into my entry, registering samples and structuring clinical chemistry results',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DATA_CAPTURE, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-extract-nanodrop-photo',
      summary: 'Extract the concentration and A260/280 values from this photo of my nanodrop display',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DATA_CAPTURE, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-process-coas',
      summary: 'Process my CoAs and import the reagent details into my entry',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DATA_CAPTURE, prompt: '', input: {type: 'none'}},
    },
  ],
  [PromptSuggestionCategory.PREDICT_STRUCTURES]: [
    {
      id: 'canned-predict-protein-structure',
      summary: 'Predict the 3D structure of my novel protein P-481',
      action: {type: 'NEW_CHAT_PLACEHOLDER', agentMode: 'PREDICT', prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-model-complex-between-proteins',
      summary: 'Model the complex between proteins A-243 and A-546',
      action: {type: 'NEW_CHAT_PLACEHOLDER', agentMode: 'PREDICT', prompt: '', input: {type: 'none'}},
    },
  ],
};

export function getCannedPromptSuggestions(category: PromptSuggestionCategory): Array<PromptSuggestion> {
  return CANNED_SUGGESTIONS_BY_SECTION[category];
}

export function useGetPromptSuggestions(): Array<PromptSuggestion | null> {
  const modelHubSuggestions = useGetSuggestions(
    // Avoid re-querying search until user refreshes the page
    useCachedResponse('model-hub', getModelHubSuggestions)
  );
  const dataCaptureSuggestions = useGetSuggestions(getDataCaptureSuggestions);
  const askSuggestions = useGetSuggestions(
    // Avoid re-querying search until user refreshes the page
    useCachedResponse('ask', getAskSuggestions)
  );
  const deepResearchSuggestions = useGetSuggestions(getDeepResearchSuggestions);
  return useMemo(
    () => [...askSuggestions, ...deepResearchSuggestions, ...modelHubSuggestions, ...dataCaptureSuggestions],
    [askSuggestions, deepResearchSuggestions, modelHubSuggestions, dataCaptureSuggestions]
  );
}
